import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Logo } from './Logo';
import { useAuth } from '@/contexts/AuthContext';
import { NotificationBell } from './NotificationBell';
import { checkAdminStatus } from '@/services/instructorService';
import { useThemeContext } from '@/contexts/ThemeContext';

/**
 * Hamburger Menu Icon
 */
function HamburgerIcon({ isOpen }: { isOpen: boolean }) {
  return (
    <div style={{ width: '24px', height: '24px', position: 'relative' }}>
      <span
        style={{
          display: 'block',
          position: 'absolute',
          height: '2px',
          width: '100%',
          backgroundColor: 'var(--cld-color-white)',
          borderRadius: '2px',
          transition: 'all 0.25s ease',
          top: isOpen ? '11px' : '4px',
          transform: isOpen ? 'rotate(45deg)' : 'rotate(0)',
        }}
      />
      <span
        style={{
          display: 'block',
          position: 'absolute',
          height: '2px',
          width: '100%',
          backgroundColor: 'var(--cld-color-white)',
          borderRadius: '2px',
          transition: 'all 0.25s ease',
          top: '11px',
          opacity: isOpen ? 0 : 1,
        }}
      />
      <span
        style={{
          display: 'block',
          position: 'absolute',
          height: '2px',
          width: '100%',
          backgroundColor: 'var(--cld-color-white)',
          borderRadius: '2px',
          transition: 'all 0.25s ease',
          top: isOpen ? '11px' : '18px',
          transform: isOpen ? 'rotate(-45deg)' : 'rotate(0)',
        }}
      />
    </div>
  );
}

interface HeaderProps {
  /**
   * Show navigation links (Dashboard, Courses, etc.)
   * Default: true
   */
  showNav?: boolean;

  /**
   * Show user actions (notifications, sign out)
   * Default: true
   */
  showUserActions?: boolean;

  /**
   * Current active page for navigation highlighting
   */
  activePage?: 'dashboard' | 'courses' | 'feedback' | 'preferences' | 'progress' | null;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Custom content to render on the right side of header
   */
  rightContent?: React.ReactNode;
}

/**
 * Header Component
 *
 * Branded navigation header for ClosedLoopDiving application.
 * Features:
 * - Deep Ocean Blue background (--cld-color-primary)
 * - Logo with brand colors
 * - Navigation links with Tech Blue hover state
 * - User actions (notifications, sign out)
 * - Responsive layout
 */
export function Header({
  showNav = true,
  showUserActions = true,
  activePage = null,
  className = '',
  rightContent,
}: HeaderProps) {
  const navigate = useNavigate();
  const { currentUser, signOut } = useAuth();
  const { isDark, toggleTheme } = useThemeContext();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // Check admin status when user is authenticated
  useEffect(() => {
    async function checkAdmin() {
      if (currentUser) {
        try {
          const adminStatus = await checkAdminStatus();
          setIsAdmin(adminStatus);
        } catch {
          // Not an admin or error checking - silently ignore
          setIsAdmin(false);
        }
      } else {
        setIsAdmin(false);
      }
    }
    checkAdmin();
  }, [currentUser]);

  const handleSignOut = async () => {
    setMobileMenuOpen(false);
    await signOut();
    navigate('/');
  };

  const handleNavClick = () => {
    setMobileMenuOpen(false);
  };

  return (
    <nav
      className={className}
      style={{
        backgroundColor: 'var(--cld-color-primary)',
        boxShadow: 'var(--cld-shadow-md)',
        position: 'sticky',
        top: 0,
        zIndex: 'var(--cld-z-sticky)',
      }}
    >
      <div
        className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center"
        style={{
          paddingLeft: 'var(--cld-spacing-lg)',
          paddingRight: 'var(--cld-spacing-lg)',
        }}
      >
        {/* Logo and Brand */}
        <Link
          to="/dashboard"
          className="flex items-center gap-3 transition-opacity hover:opacity-90"
          style={{
            gap: 'var(--cld-spacing-md)',
            transition: 'var(--cld-transition-fast)',
            background: 'transparent',
          }}
        >
          <Logo size="small" variant="light" />
          <span
            style={{
              color: 'var(--cld-color-white)',
              fontSize: 'var(--cld-font-size-xl)',
              fontFamily: 'var(--cld-font-display)',
              fontWeight: 'var(--cld-font-weight-bold)',
              letterSpacing: 'var(--cld-letter-spacing-tight)',
            }}
          >
            Closed Loop Diving
          </span>
        </Link>

        {/* Navigation Links and Actions */}
        <div
          className="flex items-center"
          style={{
            gap: 'var(--cld-spacing-lg)',
          }}
        >
          {/* Desktop Navigation Links - Hidden on mobile */}
          {showNav && (
            <div
              className="hidden md:flex items-center"
              style={{
                gap: 'var(--cld-spacing-md)',
              }}
            >
              <Link
                to="/dashboard"
                className="font-medium transition-colors"
                style={{
                  color: activePage === 'dashboard'
                    ? 'var(--cld-color-secondary)'
                    : 'var(--cld-color-white)',
                  fontSize: 'var(--cld-font-size-base)',
                  fontFamily: 'var(--cld-font-body)',
                  fontWeight: 'var(--cld-font-weight-medium)',
                  transition: 'var(--cld-transition-fast)',
                }}
                onMouseEnter={(e) => {
                  if (activePage !== 'dashboard') {
                    e.currentTarget.style.color = 'var(--cld-color-secondary)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activePage !== 'dashboard') {
                    e.currentTarget.style.color = 'var(--cld-color-white)';
                  }
                }}
              >
                Dashboard
              </Link>

              <Link
                to="/courses"
                className="font-medium transition-colors"
                style={{
                  color: activePage === 'courses'
                    ? 'var(--cld-color-secondary)'
                    : 'var(--cld-color-white)',
                  fontSize: 'var(--cld-font-size-base)',
                  fontFamily: 'var(--cld-font-body)',
                  fontWeight: 'var(--cld-font-weight-medium)',
                  transition: 'var(--cld-transition-fast)',
                }}
                onMouseEnter={(e) => {
                  if (activePage !== 'courses') {
                    e.currentTarget.style.color = 'var(--cld-color-secondary)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activePage !== 'courses') {
                    e.currentTarget.style.color = 'var(--cld-color-white)';
                  }
                }}
              >
                Courses
              </Link>

              <Link
                to="/progress"
                className="font-medium transition-colors"
                style={{
                  color: activePage === 'progress'
                    ? 'var(--cld-color-secondary)'
                    : 'var(--cld-color-white)',
                  fontSize: 'var(--cld-font-size-base)',
                  fontFamily: 'var(--cld-font-body)',
                  fontWeight: 'var(--cld-font-weight-medium)',
                  transition: 'var(--cld-transition-fast)',
                }}
                onMouseEnter={(e) => {
                  if (activePage !== 'progress') {
                    e.currentTarget.style.color = 'var(--cld-color-secondary)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activePage !== 'progress') {
                    e.currentTarget.style.color = 'var(--cld-color-white)';
                  }
                }}
              >
                Progress
              </Link>

              <Link
                to="/feedback"
                className="font-medium transition-colors"
                style={{
                  color: activePage === 'feedback'
                    ? 'var(--cld-color-secondary)'
                    : 'var(--cld-color-white)',
                  fontSize: 'var(--cld-font-size-base)',
                  fontFamily: 'var(--cld-font-body)',
                  fontWeight: 'var(--cld-font-weight-medium)',
                  transition: 'var(--cld-transition-fast)',
                }}
                onMouseEnter={(e) => {
                  if (activePage !== 'feedback') {
                    e.currentTarget.style.color = 'var(--cld-color-secondary)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activePage !== 'feedback') {
                    e.currentTarget.style.color = 'var(--cld-color-white)';
                  }
                }}
              >
                Feedback
              </Link>

              {/* Admin Link - Only shown to admins */}
              {isAdmin && (
                <Link
                  to="/admin"
                  className="font-medium transition-colors"
                  style={{
                    color: 'var(--cld-color-accent)',
                    fontSize: 'var(--cld-font-size-base)',
                    fontFamily: 'var(--cld-font-body)',
                    fontWeight: 'var(--cld-font-weight-medium)',
                    transition: 'var(--cld-transition-fast)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = '#e07700';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = 'var(--cld-color-accent)';
                  }}
                >
                  Admin
                </Link>
              )}
            </div>
          )}

          {/* Custom Right Content */}
          {rightContent}

          {/* User Actions */}
          {showUserActions && currentUser && (
            <div
              className="flex items-center"
              style={{
                gap: 'var(--cld-spacing-xs)', // Reduced from md to xs for tighter spacing
              }}
            >
              <NotificationBell uid={currentUser.uid} />

              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--cld-color-white)',
                  transition: 'var(--cld-transition-fast)',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '4px',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = 'var(--cld-color-secondary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = 'var(--cld-color-white)';
                }}
              >
                {isDark ? (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="5" />
                    <line x1="12" y1="1" x2="12" y2="3" />
                    <line x1="12" y1="21" x2="12" y2="23" />
                    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                    <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                    <line x1="1" y1="12" x2="3" y2="12" />
                    <line x1="21" y1="12" x2="23" y2="12" />
                    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                    <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
                  </svg>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                  </svg>
                )}
              </button>

              {/* Settings/Preferences Link - Always visible */}
              <Link
                to="/preferences"
                title="Preferences"
                style={{
                  color: activePage === 'preferences'
                    ? 'var(--cld-color-secondary)'
                    : 'var(--cld-color-white)',
                  transition: 'var(--cld-transition-fast)',
                  display: 'flex',
                  alignItems: 'center',
                  padding: '4px', // Compact touch target
                }}
                onMouseEnter={(e) => {
                  if (activePage !== 'preferences') {
                    e.currentTarget.style.color = 'var(--cld-color-secondary)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activePage !== 'preferences') {
                    e.currentTarget.style.color = 'var(--cld-color-white)';
                  }
                }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
              </Link>

              {/* Sign Out - Desktop only */}
              <button
                onClick={handleSignOut}
                className="hidden md:block px-4 py-2 rounded-md font-medium transition-all"
                style={{
                  backgroundColor: 'var(--cld-color-accent)',
                  color: 'var(--cld-color-white)',
                  fontSize: 'var(--cld-font-size-sm)',
                  fontFamily: 'var(--cld-font-body)',
                  fontWeight: 'var(--cld-font-weight-semibold)',
                  borderRadius: 'var(--cld-radius-md)',
                  paddingLeft: 'var(--cld-spacing-md)',
                  paddingRight: 'var(--cld-spacing-md)',
                  paddingTop: 'var(--cld-spacing-sm)',
                  paddingBottom: 'var(--cld-spacing-sm)',
                  transition: 'var(--cld-transition-fast)',
                  border: 'none',
                  cursor: 'pointer',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#e07700';
                  e.currentTarget.style.boxShadow = 'var(--cld-shadow-glow-accent)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--cld-color-accent)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                Sign Out
              </button>
            </div>
          )}

          {/* Mobile Hamburger Button - Last item, right aligned */}
          {showNav && (
            <button
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle navigation menu"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                marginLeft: 'var(--cld-spacing-xs)',
              }}
            >
              <HamburgerIcon isOpen={mobileMenuOpen} />
            </button>
          )}
        </div>
      </div>

      {/* Mobile Navigation Menu - Dropdown */}
      {showNav && mobileMenuOpen && (
        <div
          className="md:hidden"
          style={{
            backgroundColor: 'var(--cld-color-primary)',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            padding: 'var(--cld-spacing-md)',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--cld-spacing-sm)' }}>
            <Link
              to="/dashboard"
              onClick={handleNavClick}
              style={{
                color: activePage === 'dashboard' ? 'var(--cld-color-secondary)' : 'var(--cld-color-white)',
                fontSize: 'var(--cld-font-size-base)',
                fontFamily: 'var(--cld-font-body)',
                fontWeight: 'var(--cld-font-weight-medium)',
                padding: 'var(--cld-spacing-sm) var(--cld-spacing-md)',
                borderRadius: 'var(--cld-radius-sm)',
                backgroundColor: activePage === 'dashboard' ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
              }}
            >
              Dashboard
            </Link>
            <Link
              to="/courses"
              onClick={handleNavClick}
              style={{
                color: activePage === 'courses' ? 'var(--cld-color-secondary)' : 'var(--cld-color-white)',
                fontSize: 'var(--cld-font-size-base)',
                fontFamily: 'var(--cld-font-body)',
                fontWeight: 'var(--cld-font-weight-medium)',
                padding: 'var(--cld-spacing-sm) var(--cld-spacing-md)',
                borderRadius: 'var(--cld-radius-sm)',
                backgroundColor: activePage === 'courses' ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
              }}
            >
              Courses
            </Link>
            <Link
              to="/progress"
              onClick={handleNavClick}
              style={{
                color: activePage === 'progress' ? 'var(--cld-color-secondary)' : 'var(--cld-color-white)',
                fontSize: 'var(--cld-font-size-base)',
                fontFamily: 'var(--cld-font-body)',
                fontWeight: 'var(--cld-font-weight-medium)',
                padding: 'var(--cld-spacing-sm) var(--cld-spacing-md)',
                borderRadius: 'var(--cld-radius-sm)',
                backgroundColor: activePage === 'progress' ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
              }}
            >
              Progress
            </Link>
            <Link
              to="/feedback"
              onClick={handleNavClick}
              style={{
                color: activePage === 'feedback' ? 'var(--cld-color-secondary)' : 'var(--cld-color-white)',
                fontSize: 'var(--cld-font-size-base)',
                fontFamily: 'var(--cld-font-body)',
                fontWeight: 'var(--cld-font-weight-medium)',
                padding: 'var(--cld-spacing-sm) var(--cld-spacing-md)',
                borderRadius: 'var(--cld-radius-sm)',
                backgroundColor: activePage === 'feedback' ? 'rgba(255, 255, 255, 0.1)' : 'transparent',
              }}
            >
              Feedback
            </Link>

            {/* Mobile Admin Link - Only shown to admins */}
            {isAdmin && (
              <Link
                to="/admin"
                onClick={handleNavClick}
                style={{
                  color: 'var(--cld-color-accent)',
                  fontSize: 'var(--cld-font-size-base)',
                  fontFamily: 'var(--cld-font-body)',
                  fontWeight: 'var(--cld-font-weight-semibold)',
                  padding: 'var(--cld-spacing-sm) var(--cld-spacing-md)',
                  borderRadius: 'var(--cld-radius-sm)',
                  backgroundColor: 'rgba(251, 133, 0, 0.1)',
                }}
              >
                Admin
              </Link>
            )}

            {/* Mobile Sign Out */}
            {currentUser && (
              <>
                <div
                  style={{
                    borderTop: '1px solid rgba(255, 255, 255, 0.1)',
                    marginTop: 'var(--cld-spacing-sm)',
                    paddingTop: 'var(--cld-spacing-sm)',
                  }}
                />
                <button
                  onClick={handleSignOut}
                  style={{
                    backgroundColor: 'var(--cld-color-accent)',
                    color: 'var(--cld-color-white)',
                    fontSize: 'var(--cld-font-size-sm)',
                    fontFamily: 'var(--cld-font-body)',
                    fontWeight: 'var(--cld-font-weight-semibold)',
                    borderRadius: 'var(--cld-radius-md)',
                    padding: 'var(--cld-spacing-sm) var(--cld-spacing-md)',
                    border: 'none',
                    cursor: 'pointer',
                    width: '100%',
                    textAlign: 'center',
                  }}
                >
                  Sign Out
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
